import React, { useMemo } from 'react';
import { Button, Popover, Tag } from 'antd';
import type File from '../../interfaces/File';
import { getContrastBackgroundColorForText } from '../../utils/colorsUtilis';
import TruncatedTooltip from '../Common/TruncatedTooltip';
import { Icon } from '../Core/Icon';
import { useTags } from '../../hooks/useTags';

interface FilesTableTagsCellProps {
  file: File;
}

const FilesTableTagsCell: React.FC<FilesTableTagsCellProps> = ({ file }) => {
  const { tags } = useTags();

  /**
   * @description Get the visible tags for the file
   */
  const visibleFileTags = useMemo(() => {
    return file.tags
      .map((tag) => tags.find((t) => t.id === tag.id))
      .filter((t): t is (typeof tags)[0] => !!t && t.fileVisible);
  }, [file.tags, tags]);

  const firstTag = useMemo(() => {
    return visibleFileTags[0];
  }, [visibleFileTags]);

  const overflowTags = useMemo(() => {
    return visibleFileTags.slice(1);
  }, [visibleFileTags]);

  return (
    <div className="flex flex-row items-center">
      {firstTag && (
        <Tag
          key={firstTag.id}
          style={{
            backgroundColor: getContrastBackgroundColorForText(firstTag.color),
          }}
        >
          <div className="flex w-32 flex-row items-center justify-center lg:justify-between">
            <div />
            <span className="max-w-28" style={{ color: firstTag.color }}>
              <TruncatedTooltip>{firstTag.title}</TruncatedTooltip>
            </span>
            {firstTag.publicTag ? (
              <Icon name="BuildingIcon" className="h-4 w-4 text-gray-dark" />
            ) : (
              <div className="w-1" />
            )}
          </div>
        </Tag>
      )}

      {overflowTags.length > 0 && (
        <Popover
          content={
            <div className="flex w-full flex-col gap-2">
              {overflowTags.map((tag) => (
                <Tag
                  key={tag.id}
                  style={{
                    backgroundColor: getContrastBackgroundColorForText(tag.color),
                  }}
                >
                  <div className="flex w-32 flex-row items-center justify-center lg:justify-between">
                    <div />
                    <span className="max-w-28" style={{ color: tag.color }}>
                      <TruncatedTooltip>{tag.title}</TruncatedTooltip>
                    </span>
                    {tag.publicTag ? (
                      <Icon name="BuildingIcon" className="h-4 w-4 text-gray-dark" />
                    ) : (
                      <div className="w-1" />
                    )}
                  </div>
                </Tag>
              ))}
            </div>
          }
          trigger="hover"
        >
          <Button size="small" shape="circle">
            <span className="text-xs">+{overflowTags.length}</span>
          </Button>
        </Popover>
      )}
    </div>
  );
};

export default FilesTableTagsCell;
